/*
  BlockLibrary — Program Library page. Two tabs:
    * Blocks    — master-detail: filterable list on the left, the
                  selected block's details + exercises editable on the
                  right (BlockDetailPane), mirroring Templates.
    * Templates — TemplateManageDialog rendered inline.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Layers, Search, X, ArrowLeft, Loader2, Star, Plus, Wand2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import UsageDrilldownDialog from "@/components/UsageDrilldownDialog";
import TemplateManageDialog from "@/components/TemplateManageDialog";
import BlockDetailPane, { STATUS_TONE } from "@/components/program-library/BlockDetailPane";


function TabButton({ active, onClick, testId, children }) {
  return (
    <button
      type="button" role="tab" aria-selected={active} onClick={onClick} data-testid={testId}
      className={
        "px-3 py-1 text-xs font-medium rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 " +
        (active ? "bg-white text-indigo-900 shadow-sm" : "text-zinc-600 hover:text-zinc-950")
      }
    >
      {children}
    </button>
  );
}


function BlockFilters({ f, setF }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
        <Input value={f.q} onChange={set("q")} placeholder="Search name, description, tag…"
               className="h-8 pl-7 pr-7 text-sm" data-testid="block-library-search" />
        {f.q && (
          <button onClick={() => setF({ ...f, q: "" })}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100"
                  data-testid="block-library-search-clear">
            <X className="h-3 w-3 text-zinc-500" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <Input value={f.service} onChange={set("service")} placeholder="Service"
               className="h-8 text-sm" data-testid="block-library-service" />
        <select value={f.status} onChange={set("status")}
                className="h-8 text-sm border border-zinc-200 rounded px-2 bg-white" data-testid="block-library-status">
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-600">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={f.mine} onChange={set("mine")} data-testid="block-library-mine" /> Mine
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={f.favOnly} onChange={set("favOnly")} data-testid="block-library-fav-only" />
          <Star className={`h-3 w-3 ${f.favOnly ? "fill-amber-400 text-amber-500" : "text-zinc-400"}`} /> Favourites
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={f.includeArchived} onChange={set("includeArchived")}
                 data-testid="block-library-include-archived" /> Archived
        </label>
      </div>
    </div>
  );
}


function BlockListItem({ b, selected, onSelect }) {
  return (
    <button
      type="button" onClick={onSelect} data-testid={`block-row-${b.id}`}
      className={cn(
        "w-full text-left rounded border px-2 py-1.5 transition-colors",
        selected ? "border-indigo-300 bg-indigo-50/60" : "border-transparent hover:bg-zinc-100",
      )}
    >
      <div className="flex items-center gap-1.5">
        {b.is_favourite && <Star className="h-3 w-3 fill-amber-400 text-amber-500 shrink-0" />}
        <span className="text-sm font-medium flex-1 truncate">{b.name}</span>
        <Badge className={`text-2xs uppercase border ${STATUS_TONE[b.status]}`}>{b.status}</Badge>
      </div>
      <div className="text-2xs text-zinc-500 flex gap-2">
        <span>{b.content_summary?.exercise_count ?? 0} exercises</span>
        {b.service && <span>{b.service}</span>}
        {(b.usage_count || 0) > 0 && <span className="text-amber-800">{b.usage_count} in use</span>}
      </div>
    </button>
  );
}


function BlocksTab({ canEdit, newOpen, setNewOpen, setTotal }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("block");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", status: "", service: "", mine: false, favOnly: false, includeArchived: false });
  const [dirty, setDirty] = useState(false);
  const [drilldownId, setDrilldownId] = useState(null);
  const seq = useRef(0);

  const select = useCallback((id, { force = false } = {}) => {
    if (!force && dirty && id !== selectedId && !window.confirm("Discard unsaved changes to this block?")) return;
    const params = new URLSearchParams(searchParams);
    if (id) params.set("block", id); else params.delete("block");
    setSearchParams(params, { replace: true });
    setDirty(false);
  }, [dirty, selectedId, searchParams, setSearchParams]);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (f.q.trim()) params.q = f.q.trim();
      if (f.status) params.status = f.status;
      if (f.service.trim()) params.service = f.service.trim();
      if (f.mine) params.mine = true;
      if (f.favOnly) params.favourites_only = true;
      if (f.includeArchived) params.include_archived = true;
      const r = await api.get("/program-blocks", { params });
      if (my !== seq.current) return;
      setRows(r.data?.blocks || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      if (my === seq.current) toast.error("Failed to load blocks", { description: e?.response?.data?.detail || e.message });
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [f, setTotal]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!selectedId && rows.length) select(rows[0].id, { force: true });
  }, [rows, selectedId, select]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 min-h-[70vh]">
      <Card className="p-2.5 rounded-lg border border-zinc-200 shadow-none flex flex-col gap-2 md:max-h-[80vh]">
        <BlockFilters f={f} setF={setF} />
        <div className="overflow-y-auto space-y-0.5 -mx-1 px-1" data-testid="block-library-list">
          {loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-zinc-500 py-6 text-center px-2">
              No blocks match. Create one with <b>New block</b>, or save any program section as a reusable block.
            </div>
          ) : rows.map((b) => (
            <BlockListItem key={b.id} b={b} selected={b.id === selectedId} onSelect={() => select(b.id)} />
          ))}
        </div>
      </Card>

      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none min-w-0">
        {selectedId ? (
          <BlockDetailPane
            key={selectedId}
            blockId={selectedId}
            canEdit={canEdit}
            onChanged={load}
            onDirtyChange={setDirty}
            onDuplicated={(id) => { load(); if (id) select(id, { force: true }); }}
            onDeleted={() => {
              setRows((rs) => rs.filter((r) => r.id !== selectedId));
              select(null, { force: true });
              load();
            }}
            onOpenUsage={setDrilldownId}
          />
        ) : (
          <div className="text-sm text-zinc-500 py-10 text-center">Pick a block on the left or create a new one.</div>
        )}
      </Card>

      {newOpen && (
        <NewBlockDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          onCreated={(block) => { setNewOpen(false); load(); select(block.id, { force: true }); }}
        />
      )}
      {drilldownId && (
        <UsageDrilldownDialog
          open={!!drilldownId}
          onOpenChange={(v) => { if (!v) setDrilldownId(null); }}
          fetchUrl={`/program-blocks/${drilldownId}/usage`}
          title={`Programs using "${rows.find((r) => r.id === drilldownId)?.name || "this block"}"`}
        />
      )}
    </div>
  );
}


export default function BlockLibrary() {
  const { user } = useAuth();
  const canEdit = ["owner", "manager"].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "templates" ? "templates" : "blocks";
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "blocks") params.delete("tab"); else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  const [total, setTotal] = useState(0);
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="program-library-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/clients" className="text-xs text-zinc-600 hover:text-zinc-950">
          <ArrowLeft className="h-3 w-3 inline mr-1" /> Back
        </Link>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Layers className="h-5 w-5 text-indigo-600" />
          Program Library
        </h1>
        {tab === "blocks" && <Badge variant="outline" className="ml-1">{total}</Badge>}
        <div className="ml-auto">
          {tab === "blocks" && canEdit && (
            <Button size="sm" onClick={() => setNewOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1"
                    data-testid="block-library-new-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New block
            </Button>
          )}
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 mb-4"
           data-testid="program-library-tabs" role="tablist">
        <TabButton active={tab === "blocks"} onClick={() => setTab("blocks")} testId="program-library-tab-blocks">
          <Layers className="h-3.5 w-3.5 inline mr-1" /> Blocks
        </TabButton>
        <TabButton active={tab === "templates"} onClick={() => setTab("templates")} testId="program-library-tab-templates">
          <Wand2 className="h-3.5 w-3.5 inline mr-1" /> Templates
        </TabButton>
      </div>

      {tab === "templates" ? (
        <TemplateManageDialog inline open onOpenChange={() => {}} />
      ) : (
        <BlocksTab canEdit={canEdit} newOpen={newOpen} setNewOpen={setNewOpen} setTotal={setTotal} />
      )}
    </div>
  );
}


/**
 * NewBlockDialog — captures minimum viable metadata (name required,
 * everything else optional) and creates a published block via
 * POST /program-blocks/blank.
 */
function NewBlockDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [service, setService] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Give the block a name first."); return; }
    setBusy(true);
    try {
      const r = await api.post("/program-blocks/blank", {
        name: trimmed,
        description: description.trim(),
        service: service.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Block created");
      onCreated?.(r.data.block);
    } catch (e) {
      toast.error("Create failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="block-new-dialog">
        <DialogHeader>
          <DialogTitle>New program block</DialogTitle>
          <DialogDescription className="text-xs">
            Creates a published block straight away. Fill in the essentials now — you can
            adjust everything later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <label className="text-xs text-zinc-700">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Post-op ACL Week 2"
                   autoFocus data-testid="block-new-name" className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-xs text-zinc-700">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      placeholder="When you'd reach for this block, key precautions, etc."
                      data-testid="block-new-description"
                      className="text-sm resize-y" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-zinc-700">Service</label>
              <Input value={service} onChange={(e) => setService(e.target.value)}
                     placeholder="pilates / onero / …"
                     data-testid="block-new-service"
                     className="h-8 text-sm" />
            </div>
            <div>
              <label className="text-xs text-zinc-700">Tags (comma-separated)</label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)}
                     placeholder="hip, balance, home"
                     data-testid="block-new-tags"
                     className="h-8 text-sm" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="block-new-create">
            {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating…</>
                  : <>Create block</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
